import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { catalogApi, type ProductSummary } from "../../api/catalog";
import { ordersApi } from "../../api/orders";
import { productUnit } from "../../lib/productUnit";
import type { ApiError } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../components/Toast";
import { packagingCodes } from "./productAttrs";

/**
 * Shared catalog ordering: whether the user can order, which products are
 * already in their draft, and the add-to-order / open-datasheet actions —
 * so the catalog list and the category page behave identically.
 */
export function useCatalogOrdering() {
  const { state: authState, hasPermission } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const canOrder = hasPermission("orders:create");
  const [orderedIds, setOrderedIds] = useState<Set<string>>(new Set());

  // Seed "already in your draft" so cards reflect their order state on load.
  useEffect(() => {
    if (!canOrder || authState.status !== "authenticated" || !authState.user.companyId) return;
    const companyId = authState.user.companyId;
    // Kupac ima najviše jednu porudžbinu u pripremi; traži se ona, ne sve u sistemu.
    void ordersApi.list({ status: "DRAFT", companyId, size: 1 })
      .then((drafts) => {
        const mine = drafts.content[0];
        const ids = (mine?.lines ?? []).map((l) => l.productId).filter((x): x is string => !!x);
        setOrderedIds(new Set(ids));
      })
      .catch(() => undefined);
  }, [canOrder, authState]);

  async function addToOrder(p: ProductSummary) {
    // A catalog product is ordered as one of its ERP packagings, chosen on its page.
    if (packagingCodes(p).length > 0 && p.categorySlug) {
      navigate(`/catalog/${p.categorySlug}/${encodeURIComponent(p.sku)}`);
      return;
    }
    if (!canOrder) {
      toast.error("Your role can't create orders. Ask a procurement user to do this.");
      return;
    }
    if (authState.status !== "authenticated" || !authState.user.companyId) {
      toast.error("You're not bound to a company — orders need a company context.");
      return;
    }
    try {
      const drafts = await ordersApi.list({
        status: "DRAFT",
        companyId: authState.user.companyId,
        size: 1,
      });
      const myDraft = drafts.content[0];
      if (myDraft) {
        await ordersApi.addLine(myDraft.id, { productId: p.id, quantity: 1, unit: productUnit(p) });
        setOrderedIds((prev) => { const n = new Set(prev); n.add(p.id); return n; });
        toast.success(`Added ${p.sku} to draft ${myDraft.orderNumber}.`);
      } else {
        const created = await ordersApi.create({
          companyId: authState.user.companyId,
          lines: [{ productId: p.id, quantity: 1, unit: productUnit(p) }],
        });
        toast.success(`Created draft ${created.orderNumber} with ${p.sku}.`);
        navigate(`/orders/${created.id}`);
      }
    } catch (e) {
      toast.error((e as ApiError).message ?? "Could not add to order.");
    }
  }

  async function openDatasheet(p: ProductSummary) {
    try {
      const detail = await catalogApi.productBySku(p.sku);
      const ds = detail.assets?.find((a) => a.kind === "DATASHEET_PDF");
      if (!ds) {
        toast.info(`Datasheet for ${p.sku} isn't uploaded yet. Spec sheet PDFs will be available once the ERP integration is live.`);
        return;
      }
      const r = await catalogApi.presignAsset(detail.id, ds.id);
      window.open(r.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error((e as ApiError).message ?? "Could not open datasheet.");
    }
  }

  return { canOrder, orderedIds, addToOrder, openDatasheet };
}
